import { Col, Row } from "antd";
import { get, set } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineFileUpload } from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Label from "shared/components/Label";
import { NAME_SPACES } from "shared/locales/constants";
import { ATTACHMENT_TYPES, RELATIONSHIP } from "utils/constants";

const Children = ({
  values,
  onFileRemove,
  setFieldValue,
  setPercent,
  disabled,
  subscription,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const children = useMemo(
    () =>
      get(subscription, "project.contact.relatedPersons", []).filter(
        (person) => {
          return (
            person.relationship === RELATIONSHIP.CHILD ||
            get(person, "identity.fields.refundOtherAccount")
          );
        },
      ),
    [subscription],
  );

  return (
    <div className="subscription-form--wrapper">
      <div className="grid--1">
        {children.map((child, index) => {
          const childAge = moment().diff(
            get(child, "identity.user.birthDate"),
            "years",
          );
          const firstName = get(child, "identity.user.firstname", "");
          const lastName = get(child, "identity.user.lastname", "");
          const childName = [firstName, lastName].filter(Boolean).join(" ");
          const title = childName
            ? `${SUBSCRIPTION.FORM.PROOF.CHILD} - ${index + 1} (${childName})`
            : `${SUBSCRIPTION.FORM.PROOF.CHILD} - ${index + 1}`;

          return (
            <Form
              type="vertical"
              title={title}
              key={`${SUBSCRIPTION.FORM.PROOF.CHILD} ${index + 1}`}
            >
              {(() => {
                // Determine document type and label based on age
                const isUnder18 = childAge < 18;
                const attachmentType = isUnder18
                  ? ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE
                  : ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE;

                const label = isUnder18
                  ? SUBSCRIPTION.FORM.PROOF.MUTUAL_MOBILITY
                      .SOCIAL_SECURITY_CERTIFICATE ||
                    "Attestation de sécurité sociale"
                  : SUBSCRIPTION.FORM.PROOF.MUTUAL_MOBILITY
                      .STUDENT_ENROLLMENT_CERTIFICATE ||
                    "Attestation de scolarité";

                return (
                  <Row gutter={[16, 16]}>
                    <Col lg={24} xs={24}>
                      <div className="subscription-form--group-label">
                        <Label
                          label={label}
                          icon={<MdOutlineFileUpload size={16} />}
                          required={true}
                        />
                      </div>
                    </Col>
                    <Col lg={24} xs={24}>
                      <div className="subscription-form--custom-upload">
                        <Input
                          type="file"
                          variant="uploader"
                          validTypes={
                            SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE
                          }
                          onUpload={(fileList) => {
                            setFieldValue(
                              `proof.children.${get(child, "identity.user.id")}.${attachmentType}`,
                              fileList,
                            );
                            setPercent(
                              set(
                                values,
                                `proof.children.${get(
                                  child,
                                  "identity.user.id",
                                )}.${attachmentType}`,
                                fileList,
                              ),
                            );
                          }}
                          onFileRemove={onFileRemove}
                          fileList={get(
                            values,
                            `proof.children.${get(child, "identity.user.id")}.${attachmentType}`,
                            [],
                          )}
                        >
                          <Row align="center">
                            <Button type="primary--link">
                              {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                            </Button>
                          </Row>
                        </Input>
                      </div>
                    </Col>
                  </Row>
                );
              })()}
              {get(child, "identity.fields.refundOtherAccount") && (
                <Row gutter={[16, 16]}>
                  <Col lg={24} xs={24}>
                    <div className="subscription-form--group-label">
                      <Label
                        label={SUBSCRIPTION.FORM.PROOF.MUTUAL_MOBILITY.RIB}
                        icon={<MdOutlineFileUpload size={16} />}
                        required={true}
                      />
                    </div>
                  </Col>
                  <Col lg={24} xs={24}>
                    <div className="subscription-form--custom-upload">
                      <Input
                        type="file"
                        variant="uploader"
                        validTypes={SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE}
                        onUpload={(fileList) => {
                          setFieldValue(
                            `proof.children.${get(child, "identity.user.id")}.${
                              ATTACHMENT_TYPES.PAYMENT_ACCOUNT
                            }`,
                            fileList,
                          );
                          setPercent(
                            set(
                              values,
                              `proof.children.${get(
                                child,
                                "identity.user.id",
                              )}.${ATTACHMENT_TYPES.PAYMENT_ACCOUNT}`,
                              fileList,
                            ),
                          );
                        }}
                        onFileRemove={onFileRemove}
                        fileList={get(
                          values,
                          `proof.children.${get(child, "identity.user.id")}.${
                            ATTACHMENT_TYPES.PAYMENT_ACCOUNT
                          }`,
                          [],
                        )}
                      >
                        <Row align="center">
                          <Button type="primary--link">
                            {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                          </Button>
                        </Row>
                      </Input>
                    </div>
                  </Col>
                </Row>
              )}
            </Form>
          );
        })}
      </div>
    </div>
  );
};

export default Children;
